<template>
    <div class="correction" :class="{ 'correction--reviewer-card': reviewerCard }">
        <div class="correction__top">
            <div class="correction__states">
                <span
                    v-if="correction.is_global && !reviewerCard"
                    v-tooltip="'This has to be applied to all instances of these words.'"
                    style="color: var(--primaryColor); margin-right: 10px;"
                >
                    ⚠
                </span>
                <span
                    class="correction__current-state"
                    :class="
                        `correction__current-state correction__current-state--${correction.state.name}`
                    "
                >
                    <svg
                        v-if="
                            reviewerCard &&
                            ['in_progress', 'fixed'].includes(correction.state.name)
                        "
                        class="correction__current-state-icon correction__current-state-icon--alert"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                    >
                        <path d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                        <path d="M12 9v4" />
                        <path d="M12 17h.01" />
                    </svg>
                    <svg
                        v-else-if="reviewerCard"
                        class="correction__current-state-icon correction__current-state-icon--approved"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                    >
                        <circle cx="12" cy="12" r="10" />
                        <path d="m8 12 2.5 2.5L16 9" />
                    </svg>
                    {{ correction.state.label }}
                </span>
                <CorrectionsTransition
                    v-for="transition in correction.transitions"
                    :key="transition.target"
                    :transition="transition"
                    :correction-id="correction.id"
                    :class="{
                        'correction__secondary-transition':
                            reviewerCard && isSecondaryTransition(transition),
                    }"
                    @update="onUpdate"
                />
                <button
                    v-if="
                        reviewerCard &&
                        correction.state.name === 'approved' &&
                        Number.isInteger(correction.page) &&
                        correction.page >= 1
                    "
                    type="button"
                    class="correction__approved-view"
                    @click="viewInBook"
                >
                    {{ $t('corrections.item.viewInBook') }}
                </button>
            </div>
            <CorrectionDelete
                v-if="correction.permissions.destroy"
                :correction="correction"
                @delete="onCorrectionDeleted"
            />
        </div>
        <div class="correction__text">
            <div class="text__entity-wrap">
                <span
                    class="text__entity"
                    :class="{
                        'is-active': isActive
                    }"
                    @click.prevent="select(correction.book_part_v2.id, correction.id)"
                >
                    {{ correction.book_part_v2.raw_text }}
                </span>
            </div>
        </div>
        <div v-if="reviewerCard && correction.book_part_v2.audio" class="correction__player">
            <CorrectionsCardPlayer :src="correction.book_part_v2.audio" />
        </div>
        <div v-else-if="isReviewer" class="correction__player">
            <PlayerSimple class="player--round" :src="correction.book_part_v2.audio" />
        </div>
        <div class="correction__bottom">
            <CorrectionsComment :text="correction.comment" />
            <div class="correction__metadata">
                <span class="correction__timestamp">
                    {{ formatLocaleTimestamp(correction.modified, $i18n.locale) }}
                </span>
                <button
                    v-if="correction.thread && correction.count - 1"
                    type="button"
                    class="correction__thread"
                    @click="openThread"
                >
                    <span class="link correction__thread-link">
                        {{ correction.count - 1 }}
                        {{ pluralize(correction.count - 1, ['comment', 'comments', 'comments']) }} >
                    </span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { PlayerSimple } from '@speechki-frontend/shared/ui/Player';
import CorrectionDelete from '../CorrectionsDelete/CorrectionsDelete';
import CorrectionsComment from '../CorrectionsComment/CorrectionsComment';
import CorrectionsTransition from '../CorrectionsTransition/CorrectionsTransition';
import CorrectionsCardPlayer from '../CorrectionsCardPlayer/CorrectionsCardPlayer';

import { mapGetters, mapState } from 'vuex';

import { formatLocaleTimestamp, pluralize } from '@speechki-frontend/shared/helpers/functions';

// Backend transition names (`target` field, actually the FSM transition's
// method name — see CorrectionFSM/`_collect` in speechki-core) that should
// be styled as secondary/outlined actions on the reviewer card.
// `return_correction` -> "Reject this update", `discard` -> "Discard this update".
const SECONDARY_TRANSITION_TARGETS = ['return_correction', 'discard'];

export default {
    name: 'CorrectionsListItem',
    components: {
        PlayerSimple,
        CorrectionDelete,
        CorrectionsComment,
        CorrectionsTransition,
        CorrectionsCardPlayer,
    },
    props: {
        correction: {
            type: Object,
            required: true
        },
        isActive: {
            type: Boolean,
            default: false
        },
        reviewerCard: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        ...mapGetters(['isAdmin', 'isGuestAccess']),
        ...mapGetters('guestAccess', ['isInGroup']),
        ...mapState({
            book: state => state.book.book,
            user: state => state.auth.user
        }),

        isReviewer() {
            if (this.isGuestAccess) {
                return this.isInGroup('reviewer');
            }

            return this.book.reviewers.includes(this.user.pk);
        }
    },
    methods: {
        formatLocaleTimestamp,
        pluralize,
        isSecondaryTransition(transition) {
            return SECONDARY_TRANSITION_TARGETS.includes(transition.target);
        },
        select(id, correctionId) {
            this.$emit('selected', {
                id,
                localId: this.correction.book_part_v2.id,
                immutable: false,
                correction: correctionId
            });
        },
        viewInBook() {
            this.$emit('view-in-book', {
                partId: this.correction.book_part_v2.id,
                page: this.correction.page,
            });
        },
        onUpdate(id) {
            this.$emit(
                'update',
                this.reviewerCard ? { id, correctionId: this.correction.id } : id
            );
        },
        onCorrectionDeleted(id) {
            this.$emit('deleted', id);
        },
        openThread() {
            this.$emit('openThread', this.correction);
        }
    }
};
</script>

<style src="./CorrectionsListItem.scss" lang="scss"></style>
